/**
 * Presentation v2 document converter.
 *
 * v2 (persisted standard):
 * {
 *   schemaVersion: 2,
 *   name: "Presentation 1",
 *   roomId: "1234",
 *   updatedAt: "2026-09-25T...",
 *   configuration: { effects, screen, template, style, video, backgroundImage },
 *   groups: [
 *     { id, songId, name, configuration, pages: [
 *       { id: "_blank", name: "Blank", editable: false, configuration, content: "" },
 *       { id, name, editable, configuration, content }
 *     ]}
 *   ]
 * }
 *
 * v1 (legacy): PresentationGroup { playlistId, presentations: [{ songId, title,
 *   lines, pages: [{ content, title, ..., background, effect, videoId, editable }],
 *   groupSize, background, backgroundType, backgroundTransition }] }
 *
 * Cascade: page.configuration <- group.configuration <- doc.configuration.
 * Blank pages (`_blank`) are transition separators: always black, never editable,
 * auto-inserted at each group start when missing.
 */

export const SCHEMA_VERSION = 2;
export const BLANK_PAGE_ID = '_blank';

import { normalizeLayers, synthesizeLayersFromLegacy, flattenLayers, createTextBox } from './presentationLayers.js';
import { getVideoId, getYouTubeUrl } from './youtube.js';
import { detectSlideCategory } from './slideCategories.js';

export function defaultConfiguration() {
  return {
    effects: true,
    screen: '4:3',
    template: '',
    style: {},
    video: '',
    backgroundImage: '',
  };
}

export function createBlankPage() {
  return {
    id: BLANK_PAGE_ID,
    name: 'Blank',
    editable: false,
    configuration: {},
    content: '',
    layers: [],
  };
}

function newId() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    // fallback abaixo
  }
  return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

function isBlankLike(page) {
  if (!page || typeof page !== 'object') return false;
  if (page.id === BLANK_PAGE_ID) return true;
  const content = page.content ?? '';
  return content === '' && page.editable === false;
}

function pageToV2(page) {
  const p = page && typeof page === 'object' ? page : {};
  // Blank de transição: normaliza sempre para o id canônico `_blank`
  if (isBlankLike(p)) return createBlankPage();
  const { background, backgroundType, backgroundTransition, effect, videoId, style, ...rest } = p;
  // Aceita entrada v1 (chaves planas) e v2 (já aninhadas): planas vencem.
  const nested = (p.configuration && typeof p.configuration === 'object') ? p.configuration : {};
  const configuration = { ...nested };
  if (background !== undefined && background !== '' && background !== null) {
    configuration.background = background;
  } else if (background === null || background === '') {
    delete configuration.background;
  }
  if (backgroundType) {
    configuration.backgroundType = backgroundType;
  } else if (backgroundType === null || backgroundType === '') {
    delete configuration.backgroundType;
  }
  if (backgroundTransition) configuration.backgroundTransition = backgroundTransition;
  if (effect !== undefined) configuration.effect = effect;
  if (videoId) {
    const vid = getVideoId(videoId) || (videoId.length === 11 ? videoId : '');
    if (vid) {
      configuration.videoId = vid;
    } else {
      delete configuration.videoId;
    }
  } else if (videoId === null || videoId === '') {
    delete configuration.videoId;
  }
  configuration.style = { fontSize: '64px', color: '#FFFFFF', textAlign: 'center', ...(nested.style || {}), ...((style && typeof style === 'object') ? style : {}) };
  // ajuste de fundo (opacidade/tamanho/posição): top-level vence o aninhado
  const topAdjust = (p.backgroundAdjust && typeof p.backgroundAdjust === 'object') ? p.backgroundAdjust : null;
  const nestedAdjust = (nested.backgroundAdjust && typeof nested.backgroundAdjust === 'object') ? nested.backgroundAdjust : null;
  if (topAdjust || nestedAdjust) configuration.backgroundAdjust = { ...(nestedAdjust || {}), ...(topAdjust || {}) };
  // layers são fonte única: quando presentes, os campos planos derivam delas
  const normLayers = (Array.isArray(p.layers) && p.layers.length > 0) ? normalizeLayers(p.layers) : null;
  const flatLayers = normLayers ? flattenLayers(normLayers) : null;
  if (flatLayers) {
    if (flatLayers.background) {
      configuration.background = flatLayers.background;
    } else {
      delete configuration.background;
    }
    if (Object.keys(flatLayers.style || {}).length > 0) {
      configuration.style = { ...configuration.style, ...flatLayers.style };
    }
  }
  const cat = p.category || nested.category || detectSlideCategory(p) || detectSlideCategory(p.content);
  if (cat) configuration.category = cat;
  return {
    id: p.id || newId(),
    name: p.name ?? p.title ?? '',
    editable: p.editable !== false,
    // pular no ao vivo sem deletar (blank nunca é pulável: sempre projetada)
    ...(p.skipped ? { skipped: true } : {}),
    ...(cat ? { category: cat } : {}),
    configuration,
    content: flatLayers ? flatLayers.content : (p.content ?? ''),
    // layers (Photoshop-like): prontas ou sintetizadas do legado
    layers: normLayers ?? synthesizeLayersFromLegacy({ ...p, style: configuration.style }),
    // preserva chaves legadas de exibição (title/subtitle/header) sem quebrar leitores v2
    ...(flatLayers
      ? { ...(flatLayers.title ? { title: flatLayers.title } : {}), ...(flatLayers.subtitle ? { subtitle: flatLayers.subtitle } : {}) }
      : { ...(rest.title !== undefined ? { title: rest.title } : {}), ...(rest.subtitle !== undefined ? { subtitle: rest.subtitle } : {}) }),
  };
}

function presentationToGroup(p, index) {
  const src = p && typeof p === 'object' ? p : {};
  const hasPages = Array.isArray(src.pages) && src.pages.length > 0;
  const pages = hasPages ? src.pages.map(pageToV2) : [];
  // Invariante: toda música COM páginas abre com a blank de transição.
  // Sem páginas, `lines` passa intacta para o groupPages fatiar (se expandir
  // aqui em 1-linha, o groupPages entende "já agrupado" e nunca agrupa).
  if (pages.length > 0 && pages[0].id !== BLANK_PAGE_ID) {
    pages.unshift(createBlankPage());
  }
  const groupConfig = {};
  if (src.background) groupConfig.background = src.background;
  if (src.backgroundType) groupConfig.backgroundType = src.backgroundType;
  if (src.backgroundTransition) groupConfig.backgroundTransition = src.backgroundTransition;
  
  const rawSongVideo = src.songVideo || src.configuration?.songVideo;
  if (rawSongVideo && (rawSongVideo.videoId || rawSongVideo.url)) {
    const vid = getVideoId(rawSongVideo.videoId) || getVideoId(rawSongVideo.url) || (rawSongVideo.videoId?.length === 11 ? rawSongVideo.videoId : '');
    groupConfig.songVideo = {
      ...rawSongVideo,
      videoId: vid || rawSongVideo.videoId || '',
      url: vid ? getYouTubeUrl(vid) : (rawSongVideo.url || ''),
    };
  } else if (src.backgroundType === 'video' && src.background) {
    const vid = getVideoId(src.background) || (src.background.length === 11 ? src.background : '');
    if (vid) {
      groupConfig.songVideo = {
        videoId: vid,
        url: getYouTubeUrl(vid),
        loop: true,
        start: 0,
        end: null,
      };
    }
  }
  if (src.backgroundAdjust && typeof src.backgroundAdjust === 'object') groupConfig.backgroundAdjust = { ...src.backgroundAdjust };
  if (src.configuration && typeof src.configuration === 'object' && src.configuration.backgroundAdjust) {
    groupConfig.backgroundAdjust = { ...(groupConfig.backgroundAdjust || {}), ...src.configuration.backgroundAdjust };
  }
  return {
    id: src.id || src.songId || `group_${index}`,
    songId: src.songId ?? null,
    name: src.name ?? src.title ?? `Song ${index + 1}`,
    configuration: groupConfig,
    pages,
    // lines cruas para o groupPages fatiar no cliente (quando pages vazio)
    ...(!hasPages && Array.isArray(src.lines) ? { lines: src.lines } : {}),
  };
}

/**
 * Normaliza qualquer payload (v1, v2, vazio) para o documento v2.
 * Nunca lança: entrada inválida vira doc vazio.
 */
export function normalizeToV2(doc, { playlistId = '', name = '', roomId = '' } = {}) {
  const fallback = () => ({
    schemaVersion: SCHEMA_VERSION,
    name,
    roomId,
    updatedAt: new Date().toISOString(),
    playlistId,
    configuration: defaultConfiguration(),
    groups: [],
  });

  if (!doc || typeof doc !== 'object') return fallback();

  // Já é v2: garante defaults sem mutar dados do usuário
  if (doc.schemaVersion === SCHEMA_VERSION && Array.isArray(doc.groups)) {
    return {
      ...doc,
      playlistId: doc.playlistId ?? playlistId,
      configuration: { ...defaultConfiguration(), ...(doc.configuration || {}) },
      groups: doc.groups.map((g, i) => ({
        id: g?.id ?? `group_${i}`,
        songId: g?.songId ?? null,
        name: g?.name ?? `Song ${i + 1}`,
        configuration: g?.configuration || {},
        pages: Array.isArray(g?.pages) ? g.pages.map(pageToV2) : [],
        // lines cruas sobrevivem ao re-normalize (groupPages precisa delas)
        ...(Array.isArray(g?.lines) ? { lines: g.lines } : {}),
      })),
    };
  }

  // v1: PresentationGroup { presentations: [...] }
  if (Array.isArray(doc.presentations)) {
    const out = fallback();
    out.configuration = { ...defaultConfiguration(), ...(doc.configuration || {}) };
    if (doc.name) out.name = doc.name;
    if (doc.roomId) out.roomId = doc.roomId;
    out.groups = doc.presentations.map((p, i) => presentationToGroup(p, i));
    return out;
  }

  // v1 avulso: uma Presentation solta ou array de presentations
  if (Array.isArray(doc)) {
    const out = fallback();
    out.groups = doc.map((p, i) => presentationToGroup(p, i));
    return out;
  }
  if (doc.pages || doc.lines || doc.songId || doc.title) {
    const out = fallback();
    out.groups = [presentationToGroup(doc, 0)];
    return out;
  }

  return fallback();
}

/**
 * Achata página v2 para o shape plano v1 (leitores legados / rollback).
 */
export function pageToV1(page) {
  const p = page && typeof page === 'object' ? page : {};
  const cfg = p.configuration || {};
  const topStyle = (p.style && typeof p.style === 'object') ? p.style : {};
  const mergedStyle = { fontSize: '64px', color: '#FFFFFF', textAlign: 'center', ...topStyle, ...(cfg.style || {}) };
  const topAdjust = (p.backgroundAdjust && typeof p.backgroundAdjust === 'object') ? p.backgroundAdjust : {};
  const nestedAdjust = (cfg.backgroundAdjust && typeof cfg.backgroundAdjust === 'object') ? cfg.backgroundAdjust : {};
  const mergedAdjust = { ...nestedAdjust, ...topAdjust };
  const cat = p.category || cfg.category || detectSlideCategory(p) || detectSlideCategory(p.content);
  const flat = {
    id: p.id,
    content: p.content ?? '',
    // Blank nunca projeta texto: nome só vive no doc v2 (badge do editor)
    title: p.id === BLANK_PAGE_ID ? '' : (p.title ?? p.name ?? ''),
    subtitle: p.subtitle ?? '',
    header: p.header ?? '',
    background: cfg.background ?? '',
    backgroundType: cfg.backgroundType ?? null,
    backgroundTransition: cfg.backgroundTransition ?? 'fade',
    effect: cfg.effect !== undefined ? cfg.effect : true,
    videoId: cfg.videoId ?? null,
    editable: p.id === BLANK_PAGE_ID ? false : p.editable !== false,
    // pular no ao vivo (blank nunca: sempre projetada)
    ...(p.id !== BLANK_PAGE_ID && p.skipped ? { skipped: true } : {}),
    ...(cat && p.id !== BLANK_PAGE_ID ? { category: cat } : {}),
    // ajuste de fundo (top-level espelha o aninhado para o editor interno)
    ...(Object.keys(mergedAdjust).length > 0 ? { backgroundAdjust: mergedAdjust } : {}),
    // estilo (ex. cor do texto via templates) — leitores v1 ignoram sem quebrar
    ...(Object.keys(mergedStyle).length > 0 ? { style: mergedStyle } : {}),
  };
  return {
    ...flat,
    // layers para render por caixas (sintetiza do legado quando ausentes)
    layers: (() => {
      const ls = (Array.isArray(p.layers) && p.layers.length > 0)
        ? normalizeLayers(p.layers)
        : synthesizeLayersFromLegacy(flat);
      if (Object.keys(mergedAdjust).length > 0) {
        const img = ls.find((l) => l.type === 'image');
        if (img) Object.assign(img, { ...mergedAdjust });
      }
      return ls;
    })(),
  };
}

/**
 * Converte doc v2 -> shape v1 { playlistId, presentations } para compatibilidade.
 */
export function denormalizeToV1(docV2) {
  const doc = normalizeToV2(docV2);
  return {
    playlistId: doc.playlistId || '',
    presentations: doc.groups.map((g) => {
      const rawSongVideo = g.configuration?.songVideo;
      const isVideoBg = g.configuration?.backgroundType === 'video' && g.configuration?.background;
      let songVideo = null;
      if (rawSongVideo && (rawSongVideo.videoId || rawSongVideo.url)) {
        const vid = getVideoId(rawSongVideo.videoId) || getVideoId(rawSongVideo.url) || (rawSongVideo.videoId?.length === 11 ? rawSongVideo.videoId : '');
        songVideo = {
          ...rawSongVideo,
          videoId: vid || rawSongVideo.videoId || '',
          url: vid ? getYouTubeUrl(vid) : (rawSongVideo.url || ''),
        };
      } else if (isVideoBg) {
        const vid = getVideoId(g.configuration.background) || (g.configuration.background?.length === 11 ? g.configuration.background : '');
        songVideo = {
          videoId: vid || g.configuration.background,
          url: vid ? getYouTubeUrl(vid) : g.configuration.background,
          loop: true,
          start: 0,
          end: null,
        };
      }
      return {
        id: g.id,
        songId: g.songId,
        title: g.name,
        lines: Array.isArray(g.lines) ? g.lines : [],
        groupSize: 2,
        background: g.configuration?.background ?? null,
        backgroundType: g.configuration?.backgroundType ?? null,
        backgroundTransition: g.configuration?.backgroundTransition ?? 'fade',
        songVideo,
        // ajuste de fundo da música (editor interno não tem campo próprio)
        ...(g.configuration?.backgroundAdjust ? { backgroundAdjust: { ...g.configuration.backgroundAdjust } } : {}),
        pages: g.pages.map(pageToV1),
      };
    }),
  };
}

/**
 * Constrói v2 a partir do estado interno v1 (classes Presentation/PresentationGroup),
 * preservando configuration/name/roomId de um doc v2 anterior quando houver.
 */
export function groupToV2(presentationGroup, { previous = null, playlistId = '' } = {}) {
  const prev = previous && previous.schemaVersion === SCHEMA_VERSION ? previous : null;
  const presentations = presentationGroup?.presentations || presentationGroup || [];
  const list = Array.isArray(presentations) ? presentations : [];
  return {
    schemaVersion: SCHEMA_VERSION,
    name: prev?.name ?? '',
    roomId: prev?.roomId ?? '',
    updatedAt: new Date().toISOString(),
    playlistId: presentationGroup?.playlistId ?? prev?.playlistId ?? playlistId,
    configuration: { ...defaultConfiguration(), ...(prev?.configuration || {}) },
    // templates customizados do usuário sobrevivem aos saves
    ...(prev?.templates ? { templates: prev.templates } : {}),
    groups: list.map((p, i) => {
      const g = presentationToGroup(
        typeof p === 'object' ? { ...p, pages: p.pages?.map(pageToV1) } : p, i,
      );
      // preserva configuration de grupo aplicada via template (índice a índice)
      const prevGroupConfig = prev?.groups?.[i]?.configuration ? { ...prev.groups[i].configuration } : {};
      g.configuration = { ...prevGroupConfig, ...g.configuration };
      
      const rawSV = p?.songVideo;
      if (rawSV && (rawSV.videoId || rawSV.url)) {
        const vid = getVideoId(rawSV.videoId) || getVideoId(rawSV.url) || (rawSV.videoId?.length === 11 ? rawSV.videoId : '');
        const cleanSV = {
          ...rawSV,
          videoId: vid || rawSV.videoId || '',
          url: vid ? getYouTubeUrl(vid) : (rawSV.url || ''),
        };
        g.configuration.songVideo = cleanSV;
        g.configuration.background = vid || cleanSV.url;
        g.configuration.backgroundType = 'video';
      } else {
        delete g.configuration.songVideo;
        if (g.configuration.backgroundType === 'video') {
          delete g.configuration.background;
          delete g.configuration.backgroundType;
        }
      }
      if (!p?.background) {
        delete g.configuration.background;
      }
      if (!p?.backgroundType) {
        delete g.configuration.backgroundType;
      }
      return g;
    }),
  };
}

/**
 * Refatia as páginas de conteúdo de um grupo em N linhas por slide.
 * Achata os `<br>`, descarta blanks antigas e reinsere a blank canônica.
 * Nota: configs por página das fatias mescladas não sobrevivem ao refatiamento
 * (efeito global segue valendo); fundos/vídeos por página devem ser reaplicados.
 */
export function regroupPages(pages = [], groupSize = 2) {
  const size = Math.max(1, groupSize || 2);
  const lines = [];
  (pages || []).forEach((p) => {
    if (!p || p.id === BLANK_PAGE_ID) return;
    String(p.content ?? '').split(/<br\s*\/?>/i).forEach((l) => {
      if (l.trim() !== '') lines.push({ text: l, category: p.category || null });
    });
  });
  const out = [createBlankPage()];
  let currentCategory = null;
  for (let i = 0; i < lines.length; i += size) {
    const chunk = lines.slice(i, i + size);
    for (const item of chunk) {
      const detected = detectSlideCategory(item.text) || item.category;
      if (detected) {
        currentCategory = detected;
        break;
      }
    }
    const chunkContent = chunk.map((c) => c.text).join('<br>');
    const cat = currentCategory || detectSlideCategory(chunkContent);
    out.push({
      id: newId(),
      name: '',
      editable: true,
      ...(cat ? { category: cat } : {}),
      // default de criação: 64px centralizado (padrão da maioria)
      configuration: {
        effect: true,
        style: { fontSize: '64px', color: '#FFFFFF', textAlign: 'center' },
        ...(cat ? { category: cat } : {})
      },
      content: chunkContent,
      layers: [
        createTextBox({
          name: 'Conteúdo',
          role: 'content',
          html: chunkContent,
          box: { x: 5, y: 18, w: 90 },
          style: { fontSize: '64px', color: '#FFFFFF', textAlign: 'center' }
        })
      ]
    });
  }
  return out;
}

/**
 * Remove overrides de cor inline do HTML (`<span style="...color...">`).
 * Necessário porque o stage (TipTap sem extensão de cor) descarta esses
 * estilos ao parsear, enquanto o rail (v-html) os renderiza — mesma página,
 * cores diferentes. Usado pelo reset do template Padrão.
 */
export function stripInlineColors(html) {
  if (!html || typeof html !== 'string') return html;
  // 1) remove declarações de color dentro de style="..."
  let out = html.replace(/\sstyle=(["'])(?:(?!\1).)*color(?:(?!\1).)*\1/gi, '');
  // 2) desembrulha spans que ficaram sem atributos
  out = out.replace(/<span\s*>(.*?)<\/span>/gi, '$1');
  return out;
}

/**
 * Cascata de config: página <- grupo <- global. `style` mescla raso.
 */
export function resolvePageConfig(docV2, groupId, pageId) {
  const base = { ...(docV2?.configuration || {}) };
  const group = (docV2?.groups || []).find((g) => g.id === groupId);
  const page = group?.pages?.find((p) => p.id === pageId);
  const merged = { ...base, ...(group?.configuration || {}), ...(page?.configuration || {}) };
  merged.style = { ...(base.style || {}), ...(group?.configuration?.style || {}), ...(page?.configuration?.style || {}) };
  return { config: merged, group: group || null, page: page || null };
}
